// Station client auto-update (F-STN-12; spec/10 §8, spec/14 §12): signed releases, channels, staged
// rollouts, and what each station runs.
//
//   POST  /v1/hq/station-releases                         register a signed release manifest       hq:releases
//   GET   /v1/hq/station-releases                         every release, newest first              hq:releases
//   PATCH /v1/hq/station-releases/{version}               halt / un-halt one release               hq:releases
//   GET   /v1/hq/station-rollouts                         each channel's rollout                   hq:releases
//   PUT   /v1/hq/station-rollouts/{channel}               target version, percentage, halt         hq:releases
//   GET   /v1/hq/station-fleet                            the fleet's client versions and states   hq:releases
//   GET   /v1/organizations/{o}/venues/{v}/station-clients  each PC's version, target and status   stations:read
//   GET   /v1/organizations/{o}/venues/{v}/station-installers  the setup exe and the Station and Counter MSIs  stations:read
//   POST  /v1/organizations/{o}/venues/{v}/stations/{s}/client-update/install-now
//                                                         "Update now": check, install once no session runs  stations:maintenance
//   GET   /v1/station/client-update                       the signed manifest this station should run   station
//   PUT   /v1/station/client-update-status                what this station runs and its updater state  station
//   GET   /v1/station/release-keys                        the release public keys (+ a signed key set)  station
//
// **The trust is the signature, not the server.** A release is a manifest — the MSI and the kiosk bundle, each
// with its URL, sha256 and size — signed offline by the release tool with the **Ed25519 release key**
// (`STATION_RELEASE_SIGNING_KEY`, never on the server, never committed) as a compact JWS (`alg: EdDSA`,
// `aud: clavian-station-release`). The station verifies it against the public keys **pinned in its build**
// before it downloads a byte, and checks every file's sha256 and size before anything runs. The server
// only stores the signed form and decides who gets which one; a compromised server can withhold an
// update or offer an older signed one, never a file of its own. Authenticode signing of the binaries comes
// later (the owner, 2026-10-01); until then this is the integrity.
//
// **Rotation** (`GET /v1/station/release-keys`): the station trusts its pinned keys, plus the keys of a
// **key set** JWS (`aud: clavian-station-release-keys`) that one of its pinned keys signed. A new release
// key is introduced by signing a key set {old, new} with the old key and publishing it
// (`STATION_RELEASE_KEY_SET`); the server cannot mint one.
//
// **Who gets what** (`GET /v1/station/client-update`): the venue's `station.clientChannel` — `stable`
// (default), `beta`, or `pinned` to `station.clientPinnedVersion` (HQ may lock both). A channel's rollout
// names a target version and a percentage; a station is in the rollout when the first four bytes of
// sha256(`<stationId>:<targetVersion>`) mod 100 fall under the percentage — deterministic, re-drawn per
// version, so raising the percentage only ever adds stations. A halted rollout or a halted release offers
// nothing. Nothing is offered until HQ registers a release **and** sets a rollout, so the feature is off
// on the day it ships.
//
// **Update now** (`POST …/stations/{s}/client-update/install-now`, 2026-10-07): a station checks on its own schedule
// and installs only when idle; staff may ask one paired, online station to check at once and install as soon as no
// session runs, never during one. It is the `station.client.update` command through the gateway, answered `202` with
// the command like the drawer's other actions (`realtime/client-update.ts` has the station's side and its answer).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_RELEASES, STATIONS_MAINTENANCE, STATIONS_READ } from "../permissions/index.js";
import { IsoDateTimeSchema, TimezoneSchema } from "../primitives/scalars.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { StationActionParamsSchema, StationCommandViewSchema } from "./floor.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5); a user session, a member or ' +
    'a player token, an expired token and a revoked device are each refused with `401`.';
const forbidden = problemResponse('Not HQ, no `hq:releases`, or no second factor on the session.', 'forbidden', 'second-factor-required');
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
/** The JWS audience of a signed release manifest. */
export const STATION_RELEASE_AUDIENCE = 'clavian-station-release';
/** The JWS audience of a signed release key set (rotation). */
export const STATION_RELEASE_KEY_SET_AUDIENCE = 'clavian-station-release-keys';
/** The channels a rollout runs on. `pinned` is a venue's choice, not a rollout. */
export const STATION_RELEASE_CHANNELS = ['stable', 'beta'];
export const StationReleaseChannelSchema = z.enum(STATION_RELEASE_CHANNELS).openapi('StationReleaseChannel', {
    description: 'A release channel with its own staged rollout: `stable` (every venue by default) or `beta`.',
});
/** `station.clientChannel`: a rollout channel, or `pinned` to `station.clientPinnedVersion`. */
export const STATION_CLIENT_CHANNELS = ['stable', 'beta', 'pinned'];
export const StationClientChannelSchema = z.enum(STATION_CLIENT_CHANNELS).openapi('StationClientChannel', {
    description: 'Which releases a venue’s stations follow: `stable`, `beta`, or `pinned` to one version HQ chose (spec/10 §8).',
});
/**
 * The station updater's state (spec/10 §8):
 *
 *   idle         nothing to do — running what it should, or waiting for an idle moment/its window
 *   downloading  fetching the release's files in the background (bandwidth-capped)
 *   downloaded   verified on disk; waiting to install (locked with no session ≥ 5 min, or the update window)
 *   installing   the MSI is running (the service is replaced; the helper watches the new one's health)
 *   rolled_back  the new version did not report healthy within 5 minutes; the previous one was reinstalled
 *   failed       a download, a verification or an install failed; `detail` says which (retried next check)
 */
export const STATION_CLIENT_UPDATE_STATES = ['idle', 'downloading', 'downloaded', 'installing', 'rolled_back', 'failed'];
export const StationClientUpdateStateSchema = z.enum(STATION_CLIENT_UPDATE_STATES).openapi('StationClientUpdateState', {
    description: 'What the station updater is doing: idle, downloading, downloaded, installing, rolled_back or failed (spec/10 §8).',
});
/**
 * Where an update is, in finer steps than its state (2026-10-07, spec/10 §8.3) — what the console's progress line and
 * the kiosk's Updating screen say:
 *
 *   checking     asking which release to run (an Update now check)
 *   downloading  fetching the files — `progress` says how far
 *   verifying    re-checking a file already on disk against its sha256
 *   ready        verified on disk, waiting for its moment to install (state `downloaded`)
 *   installing   the MSI is running (state `installing`)
 *   restarting   the new version is up and the helper is checking its health
 *   done         nothing to do: running what it should (state `idle`)
 *   failed / rolled_back   as the state says, with its detail
 *
 * Optional and nullable on the wire: a station built before it sends neither, and the console falls back to the state.
 */
export const STATION_CLIENT_UPDATE_PHASES = ['checking', 'downloading', 'verifying', 'ready', 'installing', 'restarting', 'done', 'failed', 'rolled_back'];
export const StationClientUpdatePhaseSchema = z.enum(STATION_CLIENT_UPDATE_PHASES).openapi('StationClientUpdatePhase', {
    description: 'Where an update is: checking, downloading, verifying, ready, installing, restarting, done, failed or rolled_back (spec/10 §8.3).',
});
/** The files an update downloads: the kiosk bundle, the target MSI, and the running version's MSI (the rollback package). */
export const STATION_CLIENT_UPDATE_FILES = ['kiosk', 'msi', 'rollback'];
export const StationClientUpdateFileProgressSchema = z
    .object({
    file: z.enum(STATION_CLIENT_UPDATE_FILES),
    /** Bytes on disk so far (the whole size once it is verified). */
    bytes: z.int().min(0),
    /** The file's size, from the signed manifest. */
    total: z.int().min(0),
})
    .openapi('StationClientUpdateFileProgress', { description: 'One release file’s download: bytes so far and its size.' });
export const StationClientUpdateProgressSchema = z
    .object({
    /** Bytes on disk across the files of this update. */
    bytes: z.int().min(0),
    /** Their total size. */
    total: z.int().min(0),
    /** `bytes` of `total`, 0–100, rounded down. */
    percent: z.int().min(0).max(100),
    files: z.array(StationClientUpdateFileProgressSchema).max(STATION_CLIENT_UPDATE_FILES.length),
    /**
     * How fast it is downloading now, in bytes per second over the last few seconds (2026-10-07) — the console shows
     * "Downloading 45% · 12 MB/s". Optional and nullable: a station built before does not send it, and it is null
     * before the first second of a download and while a file already on disk is re-checked.
     */
    bytesPerSecond: z.int().min(0).nullable().optional(),
})
    .openapi('StationClientUpdateProgress', {
    description: 'How far an update’s download is, per file and in all (spec/10 §8.3). Reported at most every 5 seconds.',
});
/**
 * How a PC boots (2026-10-07, spec/10 §14, ADR-0042):
 *
 *   local                        from its own disk — everything as before
 *   diskless_client              diskless from an image a server keeps (CCBoot and its kind), on a normal boot: what it
 *                                writes is thrown away at restart, so its updater does not install (the image is updated
 *                                instead) and its own records start empty at each boot
 *   diskless_image_maintenance   diskless, booted to change the image (a CCBoot super client): what it writes goes into
 *                                the image, so its updater installs as on a local PC — how an owner updates every PC at once
 */
export const STATION_BOOT_KINDS = ['local', 'diskless_client', 'diskless_image_maintenance'];
export const StationBootKindSchema = z.enum(STATION_BOOT_KINDS).openapi('StationBootKind', {
    description: 'How a PC boots: local, diskless_client or diskless_image_maintenance (spec/10 §14).',
});
/** What decided a PC's boot kind: detected on the PC, the PC's own setting, or the venue's `station.disklessMode`. */
export const STATION_BOOT_SOURCES = ['detected', 'station', 'venue'];
/**
 * Problems a diskless PC found with the image it booted from, as codes the console words (unknown codes are ignored):
 *
 *   player_account_missing   the player account or its automatic sign-in was not in the image, so the PC could not
 *                            sign the player in at this boot and shows no kiosk — prepare the image (spec/10 §14.4)
 */
export const STATION_BOOT_ISSUES = ['player_account_missing'];
export const StationBootSchema = z
    .object({
    kind: StationBootKindSchema,
    /** The diskless system the PC recognised (`ccboot`), or null for a local PC or a diskless one not recognised by name. */
    provider: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,31}$/)
        .nullable(),
    source: z.enum(STATION_BOOT_SOURCES),
    /** Whether the PC's own records survive a restart (always on a local PC; on a diskless one only with a persistent folder). */
    persistentRecords: z.boolean(),
    /** What is wrong with the image this PC booted from (`STATION_BOOT_ISSUES`); empty when nothing is. */
    issues: z
        .array(z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,63}$/))
        .max(8),
})
    .openapi('StationBoot', {
    description: 'How a PC boots — from its own disk or diskless from an image (CCBoot) — and what decided it (spec/10 §14).',
});
/** A station client version: the MSI's `major.minor.build[.revision]` (`1.9.41234.0`). */
export const StationClientVersionSchema = z
    .string()
    .regex(/^\d{1,5}(\.\d{1,9}){2,3}$/)
    .openapi('StationClientVersion', { description: 'A station client (MSI) version, `major.minor.build[.revision]`.', example: '1.9.41234.0' });
/** A kiosk bundle version: letters, digits, dots, `+` and `-`. */
export const StationKioskVersionSchema = z.string().regex(/^[0-9A-Za-z.+-]{1,64}$/);
/** A lower-case hex SHA-256. */
export const Sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
/** An https URL — a release file is never fetched over plain http. */
export const ReleaseHttpsUrlSchema = z
    .string()
    .max(2048)
    .url()
    .refine((value) => value.startsWith('https://'), { message: 'Release files are served over https only.' });
/** The largest file a release may carry (2 GiB). */
export const MAX_STATION_RELEASE_FILE_BYTES = 2 * 1024 * 1024 * 1024;
export const StationReleaseFileSchema = z
    .object({
    url: ReleaseHttpsUrlSchema,
    sha256: Sha256HexSchema,
    size: z.int().min(1).max(MAX_STATION_RELEASE_FILE_BYTES),
})
    .meta({ id: 'StationReleaseFile' });
export const StationReleaseKioskSchema = z
    .object({
    version: StationKioskVersionSchema,
    url: ReleaseHttpsUrlSchema,
    sha256: Sha256HexSchema,
    size: z.int().min(1).max(MAX_STATION_RELEASE_FILE_BYTES),
})
    .meta({ id: 'StationReleaseKiosk' });
/** The manifest the release key signs (the JWS payload, less its `aud`/`iat`). */
export const StationReleaseManifestSchema = z
    .object({
    version: StationClientVersionSchema,
    /** The channel the release was cut for. Informational: the rollout decides who gets it. */
    channel: StationReleaseChannelSchema,
    /**
     * The full MSI (service + agent + kiosk): **Clavian Station**. It keeps the role a PC already has, so it also updates
     * a counter PC whose client predates `counterMsi`.
     */
    msi: StationReleaseFileSchema,
    /**
     * The same build as **Clavian Counter** (F-STN-17, 2026-10-01): the MSI whose role defaults to `counter`, for the
     * venue's front-desk PC. A counter updates with it; absent on releases cut before it existed (a counter then
     * updates with `msi`, which keeps its role).
     */
    counterMsi: StationReleaseFileSchema.optional(),
    /**
     * The branded setup exe (2026-10-14): `ClavianSetup.exe`, one Burn bootstrapper wrapping both MSIs behind a window
     * where the person picks a gaming PC or the front-desk PC (spec/10 §3.1). The default download for a first
     * install; the MSIs stay for IT, imaging and the updater. Absent on releases cut before it existed.
     */
    setup: StationReleaseFileSchema.optional(),
    /** The kiosk bundle alone, swapped in without restarting the service. */
    kiosk: StationReleaseKioskSchema,
    /** The oldest service that may install this release directly; null when any may. */
    minServiceVersion: StationClientVersionSchema.nullable(),
    releasedAt: IsoDateTimeSchema,
})
    .openapi('StationReleaseManifest', {
    description: 'A station release: the Clavian Station MSI, the Clavian Counter MSI and the kiosk bundle with their hashes (spec/10 §8).',
});
/** A compact JWS (header.payload.signature, base64url). */
export const CompactJwsSchema = z
    .string()
    .max(16384)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/);
// ---------------------------------------------------------------------------------------------
// HQ: releases
// ---------------------------------------------------------------------------------------------
export const StationReleaseSchema = z
    .object({
    version: StationClientVersionSchema,
    channel: StationReleaseChannelSchema,
    manifest: StationReleaseManifestSchema,
    /** The `kid` of the release key that signed it. */
    keyId: z.string().min(1).max(128),
    /** A halted release is never offered to any station, whatever the rollouts say. */
    halted: z.boolean(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('StationRelease', { description: 'A registered, signed station release (spec/10 §8, spec/14 §12).' });
export const StationReleaseListSchema = z
    .object({ data: z.array(StationReleaseSchema) })
    .openapi('StationReleaseList', { description: 'Every registered station release, newest first.' });
export const RegisterStationReleaseSchema = z
    .object({
    /** The manifest as the release tool signed it: a compact JWS, `alg: EdDSA`, `aud: clavian-station-release`. */
    signedManifest: CompactJwsSchema,
})
    .openapi('RegisterStationRelease', { description: 'A signed release manifest to register (spec/10 §8).' });
export const registerStationReleaseRoute = createRoute({
    method: 'post',
    path: '/v1/hq/station-releases',
    operationId: 'registerStationRelease',
    tags: ['hq'],
    summary: 'Register a signed station release',
    description: 'Stores a release manifest the release tool signed with the Ed25519 release key. The server verifies the signature ' +
        'against the configured release public keys (`STATION_RELEASE_PUBLIC_KEYS`) and the manifest against its schema ' +
        'before storing it; registering offers it to nobody until a rollout targets it. One platform audit row. Platform ' +
        'scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: RegisterStationReleaseSchema } }, required: true } },
    responses: {
        201: jsonResponse(StationReleaseSchema, 'The release was registered.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        409: problemResponse('A release with this version is already registered.', 'conflict'),
        422: problemResponse('The signature does not verify, no release keys are configured, or the manifest is malformed.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listStationReleasesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/station-releases',
    operationId: 'listStationReleases',
    tags: ['hq'],
    summary: 'Every station release, newest first',
    description: 'The registered releases, newest first (at most 200). Platform scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    responses: {
        200: jsonResponse(StationReleaseListSchema, 'The releases.'),
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const StationReleaseParamsSchema = z.object({ version: StationClientVersionSchema }).openapi('StationReleaseParams');
export const UpdateStationReleaseSchema = z
    .object({ halted: z.boolean() })
    .openapi('UpdateStationRelease', { description: 'Halt a release (it is offered to no station) or resume it.' });
export const updateStationReleaseRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/station-releases/{version}',
    operationId: 'updateStationRelease',
    tags: ['hq'],
    summary: 'Halt or resume one station release',
    description: 'A halted release is offered to no station — not by a rollout, not by a pin. Stations already on it stay on it. One ' +
        'platform audit row. Platform scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    request: {
        params: StationReleaseParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateStationReleaseSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationReleaseSchema, 'The release, updated.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No release with this version.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// HQ: rollouts
// ---------------------------------------------------------------------------------------------
export const StationRolloutSchema = z
    .object({
    channel: StationReleaseChannelSchema,
    /** The version the channel rolls out; null while none is set (nothing is offered). */
    targetVersion: StationClientVersionSchema.nullable(),
    /** The share of the channel's stations offered the target, 0–100. */
    percentage: z.int().min(0).max(100),
    /** A halted rollout offers nothing; stations already updated stay updated. */
    halted: z.boolean(),
    /** When HQ last changed it; null for a channel never set. */
    updatedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationRollout', { description: 'A channel’s staged rollout: the target version and the share of stations offered it (spec/10 §8).' });
export const StationRolloutListSchema = z
    .object({ data: z.array(StationRolloutSchema) })
    .openapi('StationRolloutList', { description: 'Each channel’s rollout, `stable` then `beta`.' });
export const listStationRolloutsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/station-rollouts',
    operationId: 'listStationRollouts',
    tags: ['hq'],
    summary: 'Each channel’s station rollout',
    description: 'The rollout of every channel — a channel never set reads as no target, 0%, not halted. Platform scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    responses: {
        200: jsonResponse(StationRolloutListSchema, 'The rollouts.'),
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const StationRolloutParamsSchema = z.object({ channel: StationReleaseChannelSchema }).openapi('StationRolloutParams');
export const SetStationRolloutSchema = z
    .object({
    targetVersion: StationClientVersionSchema.nullable(),
    percentage: z.int().min(0).max(100),
    halted: z.boolean(),
})
    .openapi('SetStationRollout', { description: 'A channel’s rollout: which registered release, to what share of stations, and whether it is halted.' });
export const setStationRolloutRoute = createRoute({
    method: 'put',
    path: '/v1/hq/station-rollouts/{channel}',
    operationId: 'setStationRollout',
    tags: ['hq'],
    summary: 'Set a channel’s station rollout',
    description: 'Replaces the channel’s rollout. `targetVersion` must be a registered release (`422` otherwise); raising the ' +
        'percentage only adds stations (the bucketing is deterministic per version). One platform audit row. Platform ' +
        'scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    request: {
        params: StationRolloutParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: SetStationRolloutSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationRolloutSchema, 'The rollout, set.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        422: problemResponse('The target version is not a registered release.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// HQ: the fleet
// ---------------------------------------------------------------------------------------------
export const StationFleetVersionSchema = z
    .object({ clientVersion: StationClientVersionSchema, stations: z.int().min(0) })
    .meta({ id: 'StationFleetVersion' });
export const StationFleetStateSchema = z
    .object({ state: StationClientUpdateStateSchema, stations: z.int().min(0) })
    .meta({ id: 'StationFleetState' });
export const StationFleetSchema = z
    .object({
    /** Stations that have reported a client version. */
    reporting: z.int().min(0),
    /** By client version, most stations first. */
    versions: z.array(StationFleetVersionSchema),
    /** By updater state. */
    states: z.array(StationFleetStateSchema),
})
    .openapi('StationFleet', { description: 'The client versions and updater states across every station on the platform (spec/14 §12).' });
export const getStationFleetRoute = createRoute({
    method: 'get',
    path: '/v1/hq/station-fleet',
    operationId: 'getStationFleet',
    tags: ['hq'],
    summary: 'The fleet’s station client versions',
    description: 'How many stations run each client version, and how many are in each updater state — counts only, no organisation ' +
        'or station named. Platform scope: requires `hq:releases` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_RELEASES,
    responses: {
        200: jsonResponse(StationFleetSchema, 'The fleet distribution.'),
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Venue: each PC's client
// ---------------------------------------------------------------------------------------------
export const StationClientSchema = z
    .object({
    stationId: StationIdSchema,
    /** The MSI version the station runs; null before its first report. */
    clientVersion: StationClientVersionSchema.nullable(),
    /** The kiosk bundle version it runs; null before its first report or when it could not tell. */
    kioskVersion: StationKioskVersionSchema.nullable(),
    /** The version the server would offer it now; null when none (no rollout, halted, or it is current). */
    targetVersion: StationClientVersionSchema.nullable(),
    /** What its updater is doing; null before its first report. */
    state: StationClientUpdateStateSchema.nullable(),
    /** The updater's last detail ("sha256 mismatch", "health check failed: realtime"); null when none. */
    detail: z.string().max(500).nullable(),
    /** When the station last reported; null before its first report. */
    reportedAt: IsoDateTimeSchema.nullable(),
    /** Where the update is, in finer steps (2026-10-07); null when the station has not said (an older app). */
    phase: StationClientUpdatePhaseSchema.nullable().optional(),
    /** How far the download is, while it downloads; null otherwise. */
    progress: StationClientUpdateProgressSchema.nullable().optional(),
    /**
     * How the PC boots, as it last said (2026-10-07, spec/10 §14): a diskless client does not install updates itself —
     * the console says to update the image instead. Null when the PC has not said (an older app).
     */
    boot: StationBootSchema.nullable().optional(),
})
    .openapi('StationClient', { description: 'A station’s client version, the version it is offered, and its updater state (spec/10 §8, spec/13 §3).' });
export const StationClientListSchema = z
    .object({
    /** The venue's effective `station.clientChannel`. */
    channel: StationClientChannelSchema,
    data: z.array(StationClientSchema),
})
    .openapi('StationClientList', { description: 'Every paired station of a venue with its client version and updater state.' });
export const VenueStationClientsParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema })
    .openapi('VenueStationClientsParams');
export const listVenueStationClientsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/station-clients',
    operationId: 'listVenueStationClients',
    tags: ['stations'],
    summary: 'Each station’s client version and update status',
    description: 'Every paired station of the venue: the client and kiosk versions it runs, the version it would be offered now, and ' +
        'its updater state — for the console’s Stations list and drawer (spec/13 §3). Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: VenueStationClientsParamsSchema },
    responses: {
        200: jsonResponse(StationClientListSchema, 'The venue’s station clients.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Venue: the installers to download
// ---------------------------------------------------------------------------------------------
export const StationInstallersSchema = z
    .object({
    /** The release the downloads are, or null when none is published for the venue's channel yet. */
    version: StationClientVersionSchema.nullable(),
    /** Clavian Station, for a gaming PC (the kiosk and its lockdown); null when no release is published. */
    station: StationReleaseFileSchema.nullable(),
    /**
     * Clavian Counter, for the front-desk PC (printers, the cash drawer, Wake-on-LAN; no lockdown); null when no release
     * is published or the release predates the Counter installer.
     */
    counter: StationReleaseFileSchema.nullable(),
    /**
     * The branded setup exe (`ClavianSetup.exe`, 2026-10-14): one download for either kind of PC, the person chooses a
     * gaming PC or the front-desk PC in its window. The default for a first install; `station` and `counter` are the
     * MSIs for IT and silent installs. Null or absent when the release predates it (the console then offers the MSIs).
     */
    setup: StationReleaseFileSchema.nullable().optional(),
})
    .openapi('StationInstallers', {
    description: 'The Clavian setup exe and the Clavian Station and Clavian Counter MSIs a venue downloads to set up a PC (F-STN-01, F-STN-17; spec/10 §3.1).',
});
export const getVenueStationInstallersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/station-installers',
    operationId: 'getVenueStationInstallers',
    tags: ['stations'],
    summary: 'The Clavian Station and Clavian Counter installers to download',
    description: 'The installers for a new PC at this venue, from the release its channel settles on: the pin when the venue is ' +
        'pinned; otherwise the channel rollout’s target once it reaches every station (100 %, not halted), else the newest ' +
        'registered, un-halted release of the channel older than that target (the target itself when the channel has none). ' +
        'A PC installed from an older ' +
        'release updates itself. Both are nulls when nothing is published; `counter` is null for a release cut before the ' +
        'Counter installer. `setup` is the branded setup exe for either kind of PC (null or absent before it existed). Requires `stations:read`.',
    [PERMISSION_EXTENSION]: STATIONS_READ,
    request: { params: VenueStationClientsParamsSchema },
    responses: {
        200: jsonResponse(StationInstallersSchema, 'The installers.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Venue: Update now (2026-10-07)
// ---------------------------------------------------------------------------------------------
/**
 * What Update now is for (2026-10-07): `install_now` (the default) — install the release on this PC; `update_image` — this
 * diskless PC was started as CCBoot super client to update the image, so install here and the image keeps it
 * (spec/10 §14.3). A station built before `update_image` refuses it (`invalid_payload`).
 */
export const InstallStationClientUpdateNowSchema = z
    .object({
    reason: z.enum(['install_now', 'update_image']).optional(),
})
    .openapi('InstallStationClientUpdateNow');
export const installStationClientUpdateNowRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/client-update/install-now',
    operationId: 'installStationClientUpdateNow',
    tags: ['stations'],
    summary: 'Update a station’s client now',
    description: 'Ask one station to check for its release at once and install it as soon as no session runs — without waiting for ' +
        'its 30-minute check, five idle minutes or the venue’s update window — and never during a session: with one ' +
        'running it installs when the session ends (spec/10 §8.2, F-STN-12). Every other safety holds (the signed manifest, ' +
        'the rollback package, the health check and rollback). Issued as `station.client.update` `{ reason: install_now }` ' +
        'through the gateway; answers `202` with the command, which the console follows over the member’s ' +
        '`command.updated` to the station’s answer (`StationClientUpdateCommandResult`: up to date, started, deferred or ' +
        'checking), while the station’s client-update status shows the download and install. Refused with `409` for a ' +
        'station that is offline, not paired, run by staff or a counter PC. Requires `stations:maintenance`. Audited ' +
        '(`stations.client_update.requested`). A diskless PC (CCBoot) does not keep an update it installs; with ' +
        '`reason: update_image` staff say it was started as CCBoot super client to update the image, and it installs there ' +
        '(spec/10 §14.3). With no body the reason is `install_now`.',
    [PERMISSION_EXTENSION]: STATIONS_MAINTENANCE,
    request: {
        params: StationActionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: InstallStationClientUpdateNowSchema } }, required: false },
    },
    responses: {
        202: jsonResponse(StationCommandViewSchema, 'The command was written and published.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such station at this venue.', 'not-found'),
        409: problemResponse('The station is offline, not paired, run by staff or a counter PC: it cannot take an update now.', 'conflict'),
        429: commonProblemResponses[429],
        500: problemResponse('The gateway is not available to deliver commands right now.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// Station: what to run, what it runs, the keys
// ---------------------------------------------------------------------------------------------
/** `HH:MM-HH:MM`, venue-local; may cross midnight (`23:00-05:00`). */
export const UPDATE_WINDOW_PATTERN = /^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/;
export const StationUpdateWindowSchema = z
    .object({
    /** `HH:MM`, venue-local. */
    start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    /** `HH:MM`, venue-local; earlier than `start` means the window crosses midnight. */
    end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    /** The venue's IANA timezone. */
    timezone: TimezoneSchema,
})
    .meta({ id: 'StationUpdateWindow' });
export const StationClientUpdateQuerySchema = z
    .object({
    /** The MSI version the station runs, so the server can answer "nothing" when it is current. */
    clientVersion: StationClientVersionSchema.optional(),
})
    .openapi('StationClientUpdateQuery');
export const StationClientUpdateSchema = z
    .object({
    /** The venue's effective channel. */
    channel: StationClientChannelSchema,
    /** The version to run, or null when there is nothing to do. */
    version: StationClientVersionSchema.nullable(),
    /** The signed manifest of `version` (verify it against the pinned release keys before anything else), or null. */
    signedManifest: CompactJwsSchema.nullable(),
    /** True when `version` is the venue's pin: the one case a station installs an older version. */
    pinned: z.boolean(),
    /**
     * The signed manifest of the version the station reports running, when that version is a registered release —
     * the MSI it reinstalls if the new one does not report healthy (spec/10 §8). Null when it is not registered (or
     * nothing is offered): there is no rollback package, and the station installs the MSI only when
     * `installWithoutRollback` is true — otherwise only the kiosk bundle swaps.
     */
    rollbackSignedManifest: CompactJwsSchema.nullable(),
    /**
     * True when a release is offered (`version` is non-null) but the version the station reports running is not a
     * registered release — a station installed from a hand-built installer — so no rollback package can exist: the
     * station may then install the MSI without one (its first managed install; spec/10 §8). False otherwise, including
     * whenever `rollbackSignedManifest` is set. Optional so an older server and a newer station (or the reverse) keep
     * working: a missing value means false, which is the behaviour before this field.
     */
    installWithoutRollback: z.boolean().optional(),
    /** `station.clientUpdateWindow` with the venue's timezone, or null for "whenever the station is idle". */
    updateWindow: StationUpdateWindowSchema.nullable(),
})
    .openapi('StationClientUpdate', { description: 'The release this station should run, signed, and when it may install (spec/10 §8).' });
export const getStationClientUpdateRoute = createRoute({
    method: 'get',
    path: '/v1/station/client-update',
    operationId: 'getStationClientUpdate',
    tags: ['stations'],
    summary: 'The signed release this station should run',
    description: `${STATION_AUTHENTICATION} The venue's channel resolved to a release: the pinned version, or the channel rollout's ` +
        'target when this station falls inside its percentage (deterministic by station id) and neither the rollout nor the ' +
        'release is halted. `version` is null when there is nothing to do — no rollout, outside the percentage, halted, or ' +
        'the station already runs it. The station verifies `signedManifest` against its pinned release keys (spec/10 §8).',
    request: { query: StationClientUpdateQuerySchema },
    responses: {
        200: jsonResponse(StationClientUpdateSchema, 'What to run.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const StationClientUpdateStatusSchema = z
    .object({
    clientVersion: StationClientVersionSchema,
    /** The kiosk bundle it serves; null when it cannot tell (a build without a kiosk version file). */
    kioskVersion: StationKioskVersionSchema.nullable(),
    state: StationClientUpdateStateSchema,
    /** The version the updater is working on (downloading, installing, rolled back from); null when idle. */
    targetVersion: StationClientVersionSchema.nullable(),
    detail: z.string().max(500).nullable(),
    /** When the station observed this state. */
    at: IsoDateTimeSchema,
    /**
     * Where the update is, in finer steps (2026-10-07, spec/10 §8.3). Optional and nullable: a station built before it
     * sends nothing, and the server stores null.
     */
    phase: StationClientUpdatePhaseSchema.nullable().optional(),
    /** How far the download is, while it downloads; null (or absent) otherwise. */
    progress: StationClientUpdateProgressSchema.nullable().optional(),
    /**
     * How this PC boots (2026-10-07, spec/10 §14): local, or diskless from an image (CCBoot). Optional and nullable: a
     * station built before it sends nothing, and the server stores null.
     */
    boot: StationBootSchema.nullable().optional(),
})
    .openapi('StationClientUpdateStatus', {
    description: 'What the station runs and what its updater is doing, sent on connect and on every change — never as a heartbeat; ' +
        'while it downloads, its progress at most every 5 seconds (spec/10 §8, §8.3).',
});
export const putStationClientUpdateStatusRoute = createRoute({
    method: 'put',
    path: '/v1/station/client-update-status',
    operationId: 'putStationClientUpdateStatus',
    tags: ['stations'],
    summary: 'The station reports its client version and updater state',
    description: `${STATION_AUTHENTICATION} Replaces the server's record of the calling station's client and kiosk versions and its ` +
        'updater state (`idle`, `downloading`, `downloaded`, `installing`, `rolled_back`, `failed`), and — from stations built ' +
        'since 2026-10-07 — its `phase` and download `progress`. Sent on connect and when the state or phase changes; while ' +
        'it downloads, the progress at most every 5 seconds (each 10 points, or every 30 seconds); never as a heartbeat ' +
        '(spec/10 §8, §8.3).',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: StationClientUpdateStatusSchema } }, required: true } },
    responses: {
        204: { description: 'The report was stored.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const StationReleaseKeySchema = z
    .object({
    kty: z.literal('OKP'),
    crv: z.literal('Ed25519'),
    /** The raw 32-byte public key, base64url. */
    x: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    kid: z.string().min(1).max(128),
})
    .meta({ id: 'StationReleaseKey' });
export const StationReleaseKeysSchema = z
    .object({
    /** The release public keys the server verifies registrations with (informational for the station). */
    keys: z.array(StationReleaseKeySchema),
    /**
     * A key set signed by a release key (`aud: clavian-station-release-keys`, payload `{ keys }`): a station trusts
     * its keys only when one of its **pinned** keys signed it. Null when none is published.
     */
    signedKeySet: CompactJwsSchema.nullable(),
})
    .openapi('StationReleaseKeys', { description: 'The station release public keys, and the signed key set that rotates them (spec/10 §8).' });
export const getStationReleaseKeysRoute = createRoute({
    method: 'get',
    path: '/v1/station/release-keys',
    operationId: 'getStationReleaseKeys',
    tags: ['stations'],
    summary: 'The station release public keys',
    description: `${STATION_AUTHENTICATION} The Ed25519 public keys station releases are signed with, and a key set signed by a release ` +
        'key for rotation. The station trusts the keys pinned in its build, plus those of a key set a pinned key signed — ' +
        'never a key merely listed here (spec/10 §8).',
    responses: {
        200: jsonResponse(StationReleaseKeysSchema, 'The release keys.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-releases.js.map